'use client'

import { useState, useEffect, useRef } from 'react'

import TextPlaceholder from '@/components/ui/TextPlaceholder'

import { generateCriterionSummaries } from '@/lib/actions/comparison-ai'
import { createLogger } from '@/lib/logger'

const logger = createLogger('CriterionAiSummary')

type CriterionAiSummaryProps = {
  criterionId: number
  appSlugs: string[]
  // Cache key prefix to share cache between instances (contains sorted app slugs)
  cacheKeyPrefix: string
}

// Module-level cache to persist across component mounts/unmounts
// Key format: `${cacheKeyPrefix}-${criterionId}`
const summaryCache = new Map<string, string>()
// Track in-flight requests to prevent duplicate API calls
const pendingRequests = new Map<string, Promise<string | null>>()
// Cache terminal (non-retryable) failures to avoid repeated API calls on remount
const terminalFailures = new Map<string, string>()

const MAX_RETRIES = 2
const RETRY_DELAY_MS = 1500

export default function CriterionAiSummary({
  criterionId,
  appSlugs,
  cacheKeyPrefix,
}: CriterionAiSummaryProps) {
  const [summary, setSummary] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasMounted, setHasMounted] = useState(false)
  const mountedRef = useRef(true)

  // Store appSlugs in a ref to avoid dependency array issues
  // cacheKeyPrefix already encodes the app slugs, so we use that for effect dependencies
  const appSlugsRef = useRef(appSlugs)
  appSlugsRef.current = appSlugs

  // Ensure consistent hydration by only fetching after mount
  useEffect(() => {
    setHasMounted(true)
  }, [])

  useEffect(() => {
    // Don't fetch until after hydration is complete
    if (!hasMounted) return
    mountedRef.current = true

    async function fetchSummary() {
      const cacheKey = `${cacheKeyPrefix}-${criterionId}`

      // Short-circuit if a previous attempt ended in a terminal failure
      const terminalMsg = terminalFailures.get(cacheKey)
      if (terminalMsg) {
        logger.warn(
          `AI summary for criterion ${criterionId}: skipped (cached terminal failure: ${terminalMsg})`,
        )
        if (mountedRef.current) {
          setError('Failed to generate summary')
          setLoading(false)
        }
        return
      }

      // Check cache first
      const cached = summaryCache.get(cacheKey)
      if (cached) {
        setSummary(cached)
        setLoading(false)
        return
      }

      // Check if there's already a pending request for this criterion
      // Pipeline promises resolve with null on terminal failure (never reject)
      const pending = pendingRequests.get(cacheKey)
      if (pending) {
        const result = await pending
        if (mountedRef.current) {
          if (result) {
            setSummary(result)
          } else {
            setError('Failed to generate summary')
          }
          setLoading(false)
        }
        return
      }

      // Create pipeline promise that covers the entire fetch+retry flow
      // so all concurrent callers await the same eventual result
      const currentAppSlugs = appSlugsRef.current
      const pipelinePromise = (async (): Promise<string | null> => {
        try {
          const data = await generateCriterionSummaries({
            appSlugs: currentAppSlugs,
            criteriaIds: [criterionId],
          })

          if (!data.success) {
            // Non-retryable errors (e.g., quota exceeded) — skip retries
            if (data.retryable === false) {
              const msg = data.message || 'Non-retryable error'
              logger.warn(`AI summary for criterion ${criterionId}: ${msg}`)
              terminalFailures.set(cacheKey, msg)
              return null
            }
            throw new Error(data.message || 'Failed to generate summary')
          }

          const summaryText = data.criterionSummaries?.[0]?.summary || null
          if (summaryText) {
            summaryCache.set(cacheKey, summaryText)
            return summaryText
          }

          // null result — fall through to retry (OpenAI may have returned empty)
          throw new Error('Empty summary response')
        } catch (err) {
          // Warn, not error — empty responses are expected and retried.
          // console.error triggers the Next.js dev error overlay.
          logger.warn(`AI summary for criterion ${criterionId} failed, retrying:`, err)

          // Retry with delay — concurrent OpenAI calls occasionally fail
          for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * attempt))

            // Check cache in case another instance succeeded
            const retryCached = summaryCache.get(cacheKey)
            if (retryCached) return retryCached

            try {
              const retryData = await generateCriterionSummaries({
                appSlugs: currentAppSlugs,
                criteriaIds: [criterionId],
              })

              // Stop retrying on non-retryable errors
              if (!retryData.success && retryData.retryable === false) {
                const msg = retryData.message || 'Non-retryable error'
                logger.warn(`AI summary for criterion ${criterionId}: ${msg}`)
                terminalFailures.set(cacheKey, msg)
                return null
              }

              const retryText = retryData.success
                ? retryData.criterionSummaries?.[0]?.summary || null
                : null

              if (retryText) {
                summaryCache.set(cacheKey, retryText)
                return retryText
              }
            } catch (_retryErr) {
              // Continue to next retry
            }
          }

          const msg = 'All retries exhausted'
          terminalFailures.set(cacheKey, msg)
          return null
        } finally {
          pendingRequests.delete(cacheKey)
        }
      })()

      pendingRequests.set(cacheKey, pipelinePromise)

      const result = await pipelinePromise
      if (mountedRef.current) {
        if (result) {
          setSummary(result)
        } else {
          setError('Failed to generate summary')
        }
        setLoading(false)
      }
    }

    fetchSummary()

    return () => {
      mountedRef.current = false
    }
    // cacheKeyPrefix contains the sorted app slugs, so it changes when apps change
    // hasMounted ensures we don't fetch until after hydration
  }, [criterionId, cacheKeyPrefix, hasMounted])

  if (loading) {
    return <TextPlaceholder rows={2} />
  }

  if (error || !summary) {
    return <p className="text-13 italic text-gray-400 dark:text-zinc-600">We couldn&apos;t load this summary. Refresh to try again.</p>
  }

  const paragraphs = summary
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)

  return (
    <div className="flex flex-col gap-4">
      {paragraphs.map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
    </div>
  )
}

// Utility to clear the cache (useful for testing or when apps change)
export function clearCriterionSummaryCache(cacheKeyPrefix?: string) {
  if (cacheKeyPrefix) {
    // Clear only entries matching the prefix
    for (const key of summaryCache.keys()) {
      if (key.startsWith(cacheKeyPrefix)) {
        summaryCache.delete(key)
      }
    }
    for (const key of terminalFailures.keys()) {
      if (key.startsWith(cacheKeyPrefix)) {
        terminalFailures.delete(key)
      }
    }
  } else {
    summaryCache.clear()
    terminalFailures.clear()
  }
}
